import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import DashboardSidebar from '../../components/ui/DashboardSidebar';

import GradebookHeader from './components/GradebookHeader';
import GradebookFilters from './components/GradebookFilters';
import GradebookTable from './components/GradebookTable';
import GradebookStats from './components/GradebookStats';
import GradeDetailsModal from './components/GradeDetailsModal';
import BulkGradeEntry from './components/BulkGradeEntry';
import Button from '../../components/ui/Button';

import { Student, Assignment, GradeEntry, StudentGradeRow, GradebookFilter, ClassInfo, AcademicStructure } from './types';

const GradebookInterface = () => {
  const navigate = useNavigate();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [academicStructure, setAcademicStructure] = useState<AcademicStructure>('semester');
  const [selectedGrade, setSelectedGrade] = useState<{
    studentId: string;
    assignmentId: string;
  } | null>(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [isBulkEntryOpen, setIsBulkEntryOpen] = useState(false);
  const [selectedAssignmentForBulk, setSelectedAssignmentForBulk] = useState<Assignment | null>(
    null
  );

  const user = {
    role: 'teacher' as const,
    name: 'Sarah Johnson',
    avatar: 'https://randomuser.me/api/portraits/women/44.jpg'
  };

  const classInfo: ClassInfo = {
    id: 'class-001',
    name: 'Mathematics 10A',
    subject: 'Mathematics',
    grade: 'Grade 10',
    academicYear: '2023-2024',
    teacher: 'Sarah Johnson'
  };

  const mockStudents: Student[] = [
  {
    id: 'student-001',
    name: 'Emma Thompson',
    avatar: 'https://randomuser.me/api/portraits/women/1.jpg',
    studentId: 'STU2024001',
    email: 'emma.thompson@school.edu',
    educationLevel: 'high'
  },
  {
    id: 'student-002',
    name: 'Michael Chen',
    avatar: 'https://randomuser.me/api/portraits/men/2.jpg',
    studentId: 'STU2024002',
    email: 'michael.chen@school.edu',
    educationLevel: 'high'
  },
  {
    id: 'student-003',
    name: 'Sophia Rodriguez',
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_18e766f8c-1765181835164.png",
    studentId: 'STU2024003',
    email: 'sophia.rodriguez@school.edu',
    educationLevel: 'high'
  },
  {
    id: 'student-004',
    name: 'James Wilson',
    avatar: 'https://randomuser.me/api/portraits/men/4.jpg',
    studentId: 'STU2024004',
    email: 'james.wilson@school.edu',
    educationLevel: 'high'
  },
  {
    id: 'student-005',
    name: 'Olivia Martinez',
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1b63e4e64-1765091836315.png",
    studentId: 'STU2024005',
    email: 'olivia.martinez@school.edu',
    educationLevel: 'high'
  }];


  const mockAssignments: Assignment[] = [
  {
    id: 'assign-001',
    name: 'Algebra Quiz 1',
    type: 'quiz',
    maxScore: 10,
    weight: 10,
    dueDate: '2024-01-15',
    description: 'Basic algebra concepts including linear equations and inequalities'
  },
  {
    id: 'assign-002',
    name: 'Geometry Homework',
    type: 'homework',
    maxScore: 10,
    weight: 5,
    dueDate: '2024-01-20',
    description: 'Practice problems on triangles, circles, and polygons'
  },
  {
    id: 'assign-003',
    name: 'Midterm Exam',
    type: 'exam',
    maxScore: 10,
    weight: 30,
    dueDate: '2024-02-01',
    description: 'Comprehensive exam covering all topics from first semester'
  },
  {
    id: 'assign-004',
    name: 'Group Project',
    type: 'project',
    maxScore: 10,
    weight: 20,
    dueDate: '2024-02-15',
    description: 'Collaborative project on real-world mathematical applications'
  }];


  const [grades, setGrades] = useState<Map<string, GradeEntry>>(
    new Map([
    [
    'student-001-assign-001',
    {
      studentId: 'student-001',
      assignmentId: 'assign-001',
      score: 9.5,
      submittedDate: '2024-01-14',
      feedback: 'Excellent work! Clear understanding of concepts.',
      lastModified: '2024-01-15T10:30:00Z',
      modifiedBy: 'Sarah Johnson'
    }],

    [
    'student-001-assign-002',
    {
      studentId: 'student-001',
      assignmentId: 'assign-002',
      score: 8.5,
      submittedDate: '2024-01-19',
      feedback: 'Good effort, minor calculation errors.',
      lastModified: '2024-01-20T14:20:00Z',
      modifiedBy: 'Sarah Johnson'
    }],

    [
    'student-002-assign-001',
    {
      studentId: 'student-002',
      assignmentId: 'assign-001',
      score: 8.0,
      submittedDate: '2024-01-15',
      feedback: 'Good understanding, needs more practice.',
      lastModified: '2024-01-16T09:15:00Z',
      modifiedBy: 'Sarah Johnson'
    }],

    [
    'student-003-assign-001',
    {
      studentId: 'student-003',
      assignmentId: 'assign-001',
      score: 9.0,
      submittedDate: '2024-01-14',
      feedback: 'Very good work!',
      lastModified: '2024-01-15T11:00:00Z',
      modifiedBy: 'Sarah Johnson'
    }]]

    )
  );

  const [filters, setFilters] = useState<GradebookFilter>({
    dateRange: {
      start: '',
      end: ''
    },
    assignmentTypes: [],
    studentGroups: ['all'],
    searchQuery: ''
  });

  const mockMessages = [
  {
    id: 'msg-001',
    sender: 'Principal Anderson',
    senderRole: 'administrator',
    subject: 'Grade Submission Deadline',
    preview: 'Please ensure all grades are submitted by end of week...',
    timestamp: new Date(Date.now() - 3600000).toISOString(),
    isRead: false,
    priority: 'high' as const
  }];


  const handleNavigate = (path: string) => {
    navigate(path);
  };

  const handleGradeChange = (
  studentId: string,
  assignmentId: string,
  grade: Partial<GradeEntry>) =>
  {
    const key = `${studentId}-${assignmentId}`;
    const existingGrade = grades.get(key);

    const updatedGrade: GradeEntry = {
      studentId,
      assignmentId,
      score: grade.score ?? existingGrade?.score,
      qualification: grade.qualification ?? existingGrade?.qualification,
      submittedDate: grade.submittedDate ?? existingGrade?.submittedDate ?? new Date().toISOString(),
      feedback: grade.feedback ?? existingGrade?.feedback,
      lastModified: new Date().toISOString(),
      modifiedBy: user.name
    };

    const newGrades = new Map(grades);
    newGrades.set(key, updatedGrade);
    setGrades(newGrades);
  };

  const handleShowGradeDetails = (studentId: string, assignmentId: string) => {
    setSelectedGrade({ studentId, assignmentId });
    setIsDetailsModalOpen(true);
  };

  const handleUpdateFeedback = (feedback: string) => {
    if (selectedGrade) {
      const key = `${selectedGrade.studentId}-${selectedGrade.assignmentId}`;
      const existingGrade = grades.get(key);

      if (existingGrade) {
        handleGradeChange(selectedGrade.studentId, selectedGrade.assignmentId, {
          ...existingGrade,
          feedback
        });
      }
    }
  };

  const handleBulkSave = (bulkGrades: Map<string, Partial<GradeEntry>>) => {
    const newGrades = new Map(grades);

    bulkGrades.forEach((grade, studentId) => {
      if (selectedAssignmentForBulk) {
        const key = `${studentId}-${selectedAssignmentForBulk.id}`;
        const updatedGrade: GradeEntry = {
          studentId,
          assignmentId: selectedAssignmentForBulk.id,
          score: grade.score,
          qualification: grade.qualification,
          submittedDate: new Date().toISOString(),
          feedback: grade.feedback,
          lastModified: new Date().toISOString(),
          modifiedBy: user.name
        };
        newGrades.set(key, updatedGrade);
      }
    });

    setGrades(newGrades);
  };

  const getFilteredStudentRows = (): StudentGradeRow[] => {
    let filteredStudents = [...mockStudents];

    if (filters.searchQuery) {
      const query = filters.searchQuery.toLowerCase();
      filteredStudents = filteredStudents.filter(
        (student) =>
        student.name.toLowerCase().includes(query) ||
        student.studentId.toLowerCase().includes(query)
      );
    }

    return filteredStudents.map((student) => {
      const studentGrades = new Map<string, GradeEntry>();
      let totalScore = 0;
      let totalWeight = 0;

      mockAssignments.forEach((assignment) => {
        const key = `${student.id}-${assignment.id}`;
        const grade = grades.get(key);

        if (grade) {
          studentGrades.set(assignment.id, grade);
          if (grade.score !== undefined) {
            const normalizedScore = grade.score / assignment.maxScore * 10;
            totalScore += normalizedScore * assignment.weight;
            totalWeight += assignment.weight;
          }
        }
      });

      const average = totalWeight > 0 ? totalScore / totalWeight : undefined;

      return {
        student,
        grades: studentGrades,
        average
      };
    });
  };

  const handleExport = () => {
    console.log('Exporting gradebook data...');
  };

  const handleSettings = () => {
    console.log('Opening gradebook settings...');
  };

  const handleResetFilters = () => {
    setFilters({
      dateRange: { start: '', end: '' },
      assignmentTypes: [],
      studentGroups: ['all'],
      searchQuery: ''
    });
  };

  const handleOpenBulkEntry = (assignment: Assignment) => {
    setSelectedAssignmentForBulk(assignment);
    setIsBulkEntryOpen(true);
  };

  const studentRows = getFilteredStudentRows();
  const selectedGradeData = selectedGrade ?
  grades.get(`${selectedGrade.studentId}-${selectedGrade.assignmentId}`) :
  undefined;
  const selectedStudent = selectedGrade ?
  mockStudents.find((s) => s.id === selectedGrade.studentId) :
  undefined;
  const selectedAssignment = selectedGrade ?
  mockAssignments.find((a) => a.id === selectedGrade.assignmentId) :
  undefined;

  return (
    <>
      <Helmet>
        <title>Catalog - Platforma Scola</title>
        <meta
          name="description"
          content="Sistem de gestionare a notelor, cu structuri academice multiple și calificative specifice nivelului de învățământ" />

      </Helmet>

      <div className="min-h-screen bg-background">
        <Header user={user} notificationCount={mockMessages.filter((m) => !m.isRead).length} onNavigate={handleNavigate} />

        <DashboardSidebar user={user} isCollapsed={isSidebarCollapsed} onNavigate={handleNavigate} />

        <main
          className={`pt-16 transition-all duration-300 ${
          isSidebarCollapsed ? 'lg:pl-16' : 'lg:pl-64'}`
          }>

          <GradebookHeader
            classInfo={classInfo}
            academicStructure={academicStructure}
            onStructureChange={setAcademicStructure}
            onExport={handleExport}
            onSettings={handleSettings} />


          <GradebookFilters
            filters={filters}
            onFiltersChange={setFilters}
            onReset={handleResetFilters} />


          <div className="p-4 lg:p-6">
            <GradebookStats studentRows={studentRows} />

            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-foreground">Gestionare note</h2>
              <div className="flex items-center gap-3">
                <Button
                  variant="outline"
                  onClick={() => handleOpenBulkEntry(mockAssignments[0])}
                  iconName="Grid3x3"
                  iconPosition="left">

                  Introducere în masă
                </Button>
                <Button
                  variant="default"
                  onClick={() => navigate('/class-management-hub')}
                  iconName="ArrowLeft"
                  iconPosition="left">

                  Înapoi la clasă
                </Button>
              </div>
            </div>

            <GradebookTable
              studentRows={studentRows}
              assignments={mockAssignments}
              onGradeChange={handleGradeChange}
              onShowGradeDetails={handleShowGradeDetails}
              onStudentClick={(studentId) => console.log('Navigate to student:', studentId)}
              onAssignmentClick={(assignmentId) => {
                const assignment = mockAssignments.find((a) => a.id === assignmentId);
                if (assignment) {
                  handleOpenBulkEntry(assignment);
                }
              }} />

          </div>
        </main>

        {selectedStudent && selectedAssignment &&
        <GradeDetailsModal
          isOpen={isDetailsModalOpen}
          onClose={() => setIsDetailsModalOpen(false)}
          grade={selectedGradeData}
          assignment={selectedAssignment}
          student={selectedStudent}
          onUpdateFeedback={handleUpdateFeedback} />

        }

        {selectedAssignmentForBulk &&
        <BulkGradeEntry
          isOpen={isBulkEntryOpen}
          onClose={() => setIsBulkEntryOpen(false)}
          assignment={selectedAssignmentForBulk}
          students={mockStudents}
          onBulkSave={handleBulkSave} />

        }
      </div>
    </>);

};

export default GradebookInterface;
